import { v4 as uuidv4 } from 'uuid';

import type { ProjectChange } from './pipeline/update-engine.models';
import { getAllBuildingVersions } from './recursive-element.helpers';
import type { Baseline, Project } from '../models/project.model';
import type { IProposal, ProposalId } from '../models/proposals.model';

/**
 * Result of a single project run through {@link deduplicateProposalIdsInProject}.
 */
export type DeduplicateProposalIdsResult = {
  /** New project value when at least one proposal id was rotated, otherwise the input ref. */
  project: Project;
  /** One row per regenerated proposal id with version + before/after ids for logging. */
  rotated: ReadonlyArray<{
    versionId: string;
    previousId: ProposalId;
    nextId: ProposalId;
  }>;
};

/**
 * Whether the action change carries a `versionId` + `proposalId` pair we should remap.
 */
const isVersionScopedProposalReference = (
  change: ProjectChange,
): change is Extract<
  ProjectChange,
  { proposalId: string; versionId: string }
> => 'proposalId' in change && 'versionId' in change;

/**
 * Rewrite a single proposal id on the given version and remap version-scoped action changes
 * (`ProposalResultsRecord`, `ProposalDelete`, `ProposalUpdate`, `ProposalAppendAction`) that
 * point at the old id on the same version.
 */
const rotateProposalIdOnVersion = (
  version: Baseline,
  previousId: ProposalId,
  nextId: ProposalId,
): Baseline => {
  const proposals = (version.proposals ?? []).map((p) =>
    p.id === previousId ? { ...p, id: nextId } : p,
  );

  const actions = (version.actions ?? []).map((action) => {
    let actionChanged = false;
    const rewritten = action.changes.map((change) => {
      if (!isVersionScopedProposalReference(change)) return change;
      if (change.versionId !== version.id || change.proposalId !== previousId) {
        return change;
      }
      actionChanged = true;
      return { ...change, proposalId: nextId };
    });
    return actionChanged ? { ...action, changes: rewritten } : action;
  });

  return { ...version, proposals, actions };
};

/**
 * Detect proposal ids that appear on more than one version inside the same project and
 * regenerate the colliding ones so {@link findProposalLocation} routing becomes deterministic.
 *
 * Strategy:
 * - First-seen wins: the first version (in `getAllBuildingVersions` order) keeps the id.
 * - Every subsequent version that re-uses the same id gets a fresh uuid for that proposal.
 * - Action changes that carry a `{ versionId, proposalId }` pair on the same version are
 *   remapped to the new id; references on other versions are intentionally left alone
 *   because they would already have been broken before the rotation.
 *
 * Background: DEV-3313. Old project data (pre-DEV-3302/3305) ended up with the same proposal
 * id on multiple versions, so saves landed on the wrong version and silently no-op'd.
 */
export const deduplicateProposalIdsInProject = (
  project: Project,
): DeduplicateProposalIdsResult => {
  const versions = getAllBuildingVersions(project);
  if (versions.length < 2) return { project, rotated: [] };

  const seenIds = new Set<ProposalId>();
  const rotations: Array<{
    versionId: string;
    previousId: ProposalId;
    nextId: ProposalId;
  }> = [];

  // Walk versions in tree order; first occurrence keeps the id, later duplicates rotate.
  for (const version of versions) {
    const proposals = version.proposals;
    if (!proposals?.length) continue;
    for (const proposal of proposals) {
      if (!seenIds.has(proposal.id)) {
        seenIds.add(proposal.id);
        continue;
      }
      rotations.push({
        versionId: version.id,
        previousId: proposal.id,
        nextId: uuidv4(),
      });
    }
  }

  if (rotations.length === 0) return { project, rotated: [] };

  // Apply rotations grouped by version so each version is rebuilt at most once.
  const rotationsByVersionId = new Map<
    string,
    Array<{ previousId: ProposalId; nextId: ProposalId }>
  >();
  for (const rotation of rotations) {
    const list = rotationsByVersionId.get(rotation.versionId) ?? [];
    list.push({ previousId: rotation.previousId, nextId: rotation.nextId });
    rotationsByVersionId.set(rotation.versionId, list);
  }

  const nextProject: Project = {
    ...project,
    baselines: project.baselines.map((version) => {
      if (!version) return version;
      const versionRotations = rotationsByVersionId.get(version.id);
      if (!versionRotations?.length) return version;
      let nextVersion = version;
      for (const { previousId, nextId } of versionRotations) {
        nextVersion = rotateProposalIdOnVersion(
          nextVersion,
          previousId,
          nextId,
        );
      }
      return nextVersion;
    }),
  };

  return { project: nextProject, rotated: rotations };
};

/**
 * Returns proposal ids that appear on more than one {@link Baseline} in the list.
 */
export const getCrossVersionDuplicateProposalIdsFromVersions = (
  versions: readonly Baseline[],
): ProposalId[] => {
  const seen = new Set<ProposalId>();
  const duplicates = new Set<ProposalId>();
  for (const version of versions) {
    for (const proposal of version.proposals ?? []) {
      if (seen.has(proposal.id)) duplicates.add(proposal.id);
      else seen.add(proposal.id);
    }
  }
  return Array.from(duplicates);
};

/**
 * Whether any version in the project shares a proposal id with another version.
 * Pure check, no mutation.
 */
export const hasCrossVersionProposalIdCollision = (project: Project): boolean =>
  getCrossVersionDuplicateProposalIdsFromVersions(
    getAllBuildingVersions(project),
  ).length > 0;

/**
 * Returns the proposal id(s) that collide across versions. Empty when the project is clean.
 * Useful for validation error messages and migration logging.
 */
export const getCrossVersionDuplicateProposalIds = (
  project: Project,
): ProposalId[] =>
  getCrossVersionDuplicateProposalIdsFromVersions(
    getAllBuildingVersions(project),
  );

/** Re-exports the proposal type so callers don't need to dig out the model. */
export type { IProposal };
